import Vue from "vue";
import Vuex from "vuex";
import findCategories from "../lib/find-categories";
import findFeatureGroups from "../lib/find-feature-groups";
import findCategoryGroups from "../lib/find-category-groups";

export let store;
Vue.use(Vuex);

// helper to get data from session storage
function getFromStorage(key) {
  const data = sessionStorage.getItem(key);
  try {
    return data ? JSON.parse(data) : false;
  } catch (e) {
    // if no JSON - do not fail
    return false;
  }
}

// helper to write boolean data to session storage
function saveToStorage(key, value) {
  sessionStorage.setItem(key, JSON.stringify(value));
}

function ensureArray(value) {
  if (value && !Array.isArray(value)) {
    return value.split(",");
  }
  return value;
}

// load initial state from local storage
function initialState(namespace = "eoev", defaultValues = {}) {
  const favorites = ensureArray(getFromStorage("eoev-favorites")); // global - no namespace!
  const categories = ensureArray(getFromStorage(namespace + "-categories"));
  const location = getFromStorage(namespace + "-location");
  const features = ensureArray(getFromStorage(namespace + "-features"));
  const filterFavorites = getFromStorage(namespace + "-filter-favorites");
  const sort = getFromStorage(namespace + "-filter-sort");
  const direction = getFromStorage(namespace + "-filter-direction");
  const search = getFromStorage(namespace + "-filter-search");
  const start = getFromStorage(namespace + "-filter-start");
  const end = getFromStorage(namespace + "-filter-end");
  const selectedCity = getFromStorage(namespace + "-selected-city");
  const selectedNormalizedCity = getFromStorage(namespace + "-selected-normalized-city");
  const selectedCategoryGroup = getFromStorage(namespace + '-selected-category-group')
  const distinctEvents = getFromStorage(namespace + "-distinct-events");

  // do we have any saved values? Show stuff
  const storedValuesExist = !!(
    favorites.length ||
    categories.length ||
    location ||
    features.length ||
    filterFavorites ||
    sort ||
    direction ||
    search ||
    start ||
    end ||
    selectedCity ||
    selectedNormalizedCity ||
    selectedCategoryGroup ||
    distinctEvents !== undefined
  );

  return {
    // connection stuff
    apiUrl: defaultValues.apiUrl || "", // API base URL
    apiKey: defaultValues.apiKey || "", // API key

    // targets
    targetUri: defaultValues.targetUri || "", // target URI for web pages
    listUri: defaultValues.listUri || "", // target URI for list view

    // user storage
    favorites: favorites || [], // favorites of user - saved in local storage

    // list parameters
    firstDayOfWeek: defaultValues.firstDayOfWeek || 1,

    // search parameters
    s: search || "", // search string
    start: start || undefined, // start date
    end: end || undefined, // end date
    selectedCategories: categories || [], // selected categories of user
    selectedCategoryGroup: selectedCategoryGroup || undefined,
    selectedFeatures: features || [], // selected features of user
    selectedLocation: location || undefined, // selected location of user
    filterFavorites: filterFavorites,
    selectedCity: selectedCity,
    selectedNormalizedCity: selectedNormalizedCity,
    sort: sort || "",
    direction: direction || "",
    distinctEvents: distinctEvents ?? false,
    storedValuesExist: storedValuesExist, // flag for TYPO3

    // colors
    textColor: defaultValues.textColor || "#333",

    // map pin colors
    mapPinFillColor: defaultValues.mapPinFillColor || "#257eca",
    mapPinStrokeColor: defaultValues.mapPinStrokeColor || "#2d5069",
    mapPinFillColorActive: defaultValues.mapPinFillColorActive || "#c55c52",
    mapPinStrokeColorActive: defaultValues.mapPinStrokeColorActive || "#5d332f",
    mapAbove: defaultValues.mapAbove || 0, // pad list map with pixels above
    mapBelow: defaultValues.mapBelow || 0, // map list map with pixels below

    // saved data
    categories: [],
    featureGroups: [],
    categoryGroups: [],

    // rollover event markers
    rolloverCardLocationId: undefined, // set to location id, if mouse over card
    rolloverMapLocationId: undefined, // set to location id, if mouse over marker icon

    // helper to save labels by id
    labelsById: {}
  };
}

/**
 * Store for App.vue
 */
export default function (namespace = "eoev", defaultValues = {}) {
  store = new Vuex.Store({
    state: initialState(namespace, defaultValues),
    mutations: {
      /**
       * Initialize settings - depending on defaults
       * @param state
       * @param payload
       */
      setSettings(state, payload) {
        if (!state.sort && payload.sort){
          state.sort = payload.sort;
        }
        if (!state.direction && payload.direction){
          state.direction = payload.direction;
        }
        state.distinctEvents = payload.distinctEvents;
      },
      /**
       * add favorite to list
       * @param state
       * @param id
       */
      addFavorite(state, id) {
        if (state.favorites.indexOf(id) === -1) {
          state.favorites.push(id);
          // persist to local storage
          saveToStorage("eoev-favorites", state.favorites);
        }
      },
      /**
       * remove favorite from list
       * @param state
       * @param id
       */
      removeFavorite(state, id) {
        const pos = state.favorites.indexOf(id);
        if (pos !== -1) {
          state.favorites.splice(pos, 1);
          // persist to local storage
          saveToStorage("eoev-favorites", state.favorites);
        }
      },
      /**
       * change category selection
       * @param state
       * @param newCat
       */
      selectCategories(state, newCats) {
        state.selectedCategories = newCats || undefined;
        // persist to local storage
        saveToStorage(namespace + "-categories", state.selectedCategories);
      },
      /**
       * update selected features
       * @param state
       * @param features
       */
      updateSelectedFeatures(state, features) {
        state.selectedFeatures = features;
        // persist to local storage
        saveToStorage(namespace + "-features", state.selectedFeatures);
      },
      selectLocation(state, newLocationId) {
        state.selectedLocation = newLocationId || undefined;
        // persist to local storage
        saveToStorage(namespace + "-location", state.selectedLocation);

        // disable favorites
        if (state.filterFavorites) {
          state.filterFavorites = false;
          saveToStorage(namespace + "-filter-favorites", false);
        }
      },
      toggleFilterFavorites(state) {
        state.filterFavorites = !state.filterFavorites;
        // persist to local storage
        saveToStorage(namespace + "-filter-favorites", state.filterFavorites);
      },
      /**
       * set categories - normally called by action loadCategories
       * @param state
       * @param categories
       */
      setCategories(state, categories) {
        state.categories = categories;

        if (categories) {
          categories.forEach(category => {
            state.labelsById[category._id] = category.title;
          });
        }
      },
      /**
       * set feature groups - normally called by action loadFeatureGroups
       * @param state
       * @param featureGroups
       */
      setFeatureGroups(state, featureGroups) {
        state.featureGroups = featureGroups;

        if (featureGroups) {
          featureGroups.forEach(featureGroup => {
            featureGroup.features.forEach(feature => {
              state.labelsById[feature._id] = feature.title;
            });
          });
        }
      },
      setCategoryGroups(state, categoryGroups) {
        state.categoryGroups = categoryGroups;

        if (categoryGroups) {
          categoryGroups.forEach(categoryGroup => {
            state.labelsById[categoryGroup._id] = categoryGroup.title;
            categoryGroup.categories.forEach(category => {
              state.labelsById[category._id] = category.title;
            });
          });
        }
      },
      updateSort(state, payload) {
        state.sort = payload.sort;
        state.direction = payload.direction;

        saveToStorage(namespace + "-filter-sort", state.sort);
        saveToStorage(namespace + "-filter-direction", state.direction);
      },
      setSearch(state, search) {
        state.s = search;
        // persist to local storage
        saveToStorage(namespace + "-filter-search", state.s);
      },
      setStart(state, date) {
        state.start = date;
        // persist to local storage
        saveToStorage(namespace + "-filter-start", state.start);
      },
      setEnd(state, date) {
        state.end = date;
        // persist to local storage
        saveToStorage(namespace + "-filter-end", state.end);
      },
      setStartEnd(state, dates) {
        state.start = dates.start;
        state.end = dates.end;
        state.setDate = true;
        // persist to local storage
        saveToStorage(namespace + "-filter-setDate", state.setDate);
        saveToStorage(namespace + "-filter-start", state.start);
        saveToStorage(namespace + "-filter-end", state.end);
      },
      rolloverCardLocationId(state, id) {
        state.rolloverCardLocationId = id;
      },
      rolloverMapLocationId(state, id) {
        state.rolloverMapLocationId = id;
      },
      setSelectedCity(state, value) {
        state.selectedCity = value;
        saveToStorage(namespace + "-selected-city", value);
      },
      setSelectedNormalizedCity(state, value) {
        state.selectedNormalizedCity = value;
        saveToStorage(namespace + "-selected-normalized-city", value);
      },
      setSelectedCategoryGroup(state, value) {
        state.selectedCategoryGroup = value;
        saveToStorage(namespace + "-selected-category-group", value);
      },
      setDistinctEvents(state, value) {
        state.distinctEvents = value;
      }
    },
    actions: {
      /**
       * load categories from API if needed
       * @param state
       * @param commit
       * @returns {Promise<string[]|string|[]|*[]>}
       */
      async loadCategories({ state, commit }) {
        if (!state.categories || state.categories.length === 0) {
          try {
            commit(
              "setCategories",
              await findCategories(state.apiUrl, state.apiKey)
            );
          } catch (e) {
            console.error(e);
          }
        }
        return state.categories;
      },
      /**
       * load feature groups from API if needed
       * @param state
       * @param commit
       * @returns {Promise<[]|*[]>}
       */
      async loadFeatureGroups({ state, commit }) {
        if (!state.featureGroups || state.featureGroups.length === 0) {
          try {
            commit(
              "setFeatureGroups",
              await findFeatureGroups(state.apiUrl, state.apiKey)
            );
          } catch (e) {
            console.error(e);
          }
        }
        return state.featureGroups;
      },
      async loadCategoryGroups({ state, commit }) {
        if (!state.categoryGroups || state.categoryGroups.length === 0) {
          try {
            commit(
              "setCategoryGroups",
              await findCategoryGroups(state.apiUrl, state.apiKey)
            );
          } catch (e) {
            console.error(e);
          }
        }
        return state.categoryGroups;
      }
    }
  });
  return store;
}
